import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterAbility,
  CharacterSpell,
  StoredSpell,
  Potence,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DataUtilities from '@dndUtilities/data_utilities';

import DandDButtonBox from "@dndComponents/DandDButtonBox";
import SelectCharacterItemBox from "@dndComponents/select/SelectCharacterItemBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';

interface Props {
  character: Character;
  spell?: CharacterAbility | CharacterSpell | StoredSpell;
  processing: boolean;
  disabled: boolean;
  log_it?: boolean;
  shared: boolean;
  potence: Potence;
  onChange: (change_types: string[]) => void; 
}

const api = API.getInstance();
const data_util = DataUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CharacterEnhanceItemEffect({
  character,
  spell,
  processing,
  disabled,
  shared,
  potence,
  onChange,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [is_processing, setProcessing] = useState(false);
  const [selected_id, setSelectedId] = useState("");
  const [selected_mik_id, setSelectedMikId] = useState("");

  useEffect(() => {
    if (is_processing !== processing) {
      setProcessing(processing);
    }
    // eslint-disable-next-line
  }, [processing]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const enabled = shared || (!disabled && api.user && (character.owner_id === api.user._id || (character.campaign !== null && character.campaign.owner_id === api.user._id)));

  const renderItemSelect = () => {
    let use_mik_id = selected_mik_id;
    if (potence.enhance_ids.length === 1) {
      use_mik_id = potence.enhance_ids[0];
    }
    const mik = data_util.magic_item_keywords.find(o => o._id === use_mik_id);
    let valid_items = [...character.items];
    if (mik) {
      if (mik.base_item_ids.length > 0) {
        valid_items = valid_items.filter(o => mik.base_item_ids.includes(o.base_item_id));
      } else if (mik.base_item_types && mik.base_item_types.length > 0) {
        valid_items = valid_items.filter(o => mik.base_item_types.includes(o.item_type));
      }
    }
    // console.log(mik);
    return (
      <Grid size={6}>
        { !reloading && 
          <SelectCharacterItemBox 
            name="Selected Item"
            value={selected_id}
            character={character}
            objects={valid_items}
            disabled={mik === undefined}
            onChange={setSelectedId}
          />
        }
      </Grid>
    );
  }

  const renderButtons = () => {
    let use_mik_id = selected_mik_id;
    if (potence.enhance_ids.length === 1) {
      use_mik_id = potence.enhance_ids[0];
    }
    const mik = data_util.magic_item_keywords.find(o => o._id === use_mik_id);
    const selected_item = character.items.find(o => o.true_id === selected_id);
    return (
      <>
        <Grid
          size={{
            xs: 3
          }}>
          <DandDButtonBox
            name={ mik === undefined ? "Apply" : (selected_item && selected_item.magic_item_keyword_ids.includes(mik._id) ? "Remove" : `Apply`) }
            disabled={ !enabled || mik === undefined || selected_id === "" }
            onClick={async () => {
              const char_item = character.items.find(o => o.true_id === selected_id);
              if (char_item && mik) {
                if (char_item.magic_item_keyword_ids.includes(mik._id)) {
                  char_item.sub_char_feature_sources = 
                  char_item.sub_char_feature_sources.filter(o => o.source_id !== mik._id);
                } else {
                  await connect_util.connectMagicItemKeyword({
                    char: character, 
                    char_item_or_id: char_item, 
                    magic_item_keyword_or_id: mik._id, 
                    source_id: spell?.source_id??"",
                    char_feature_id: ""
                  });
                }
                onChange(["Items"]);
              }
            }} 
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <DandDButtonBox
            name={ `Remove All` }
            disabled={ !enabled || mik === undefined || character.items.filter(o => 
                o.magic_item_keyword_ids.includes(mik._id)).length === 0 }
            onClick={() => {
              // debugger;
              if (mik) {
                for (const char_item of character.items.filter(o => 
                  o.magic_item_keyword_ids.includes(mik._id))) {
                  if (char_item) {
                    char_item.sub_char_feature_sources = 
                      char_item.sub_char_feature_sources.filter(o => o.source_id !== mik._id);
                  }
                }
                onChange(["Items"]);
              }
            }} 
          />
        </Grid>
      </>
    );
  }

  // Select a valid item
  // Apply the mik
  // Also have a button to clear it from all items.
  // debugger;
  if (potence.enhance_ids.length === 1) {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 4
          }}>

        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 8
          }}>
          { renderItemSelect() }
          { renderButtons() }
        </Grid>
      </Grid>
    );
  } else {
    // console.log(potence);
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 4
          }}>
          <SelectObjectBox
            name="Select Option"
            data_type="magic_item_keyword"
            value={selected_mik_id}
            filter={{ "_id": { "in": potence.enhance_ids }}}
            onChange={(changed: string) => {
              if (selected_id !== "") {
                // There was an item selected already, make sure it's valid for the new selected mik
                const new_mik = data_util.magic_item_keywords.find(o => o._id === changed);
                if (new_mik) {
                  let valid_items = [...character.items];
                  if (new_mik.base_item_ids.length > 0) {
                    valid_items = valid_items.filter(o => new_mik.base_item_ids.includes(o.base_item_id));
                  } else if (new_mik.base_item_types && new_mik.base_item_types.length > 0) {
                    valid_items = valid_items.filter(o => new_mik.base_item_types.includes(o.item_type));
                  }
                  if (valid_items.filter(o => o.true_id === selected_id).length === 0) {
                    setSelectedId("");
                  }
                }
              }
              setSelectedMikId(changed);
              setReloading(true);
            }}
          />
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 8
          }}>
          { renderItemSelect() }
          { renderButtons() }
        </Grid>
      </Grid>
    );
  }
}
